import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Flipped, Flipper } from 'react-flip-toolkit';
import { useHits } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useResizeObserver } from '@sorare/core/src/hooks/ui/useResizeObserver';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const itemWidth = 185;
const doubleUnitGap = 16;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  min-height: 128px;
  background: rgba(var(--c-rgb-black), 0.4);
`;

const LoadingIndicatorContainer = styled.div`
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
`;

export const GridOverlayLoadingIndicator = () => {
  return (
    <LoadingOverlay>
      <LoadingIndicatorContainer>
        <LoadingIndicator />
      </LoadingIndicatorContainer>
    </LoadingOverlay>
  );
};

const gridCss = css<{ $columns?: number }>`
  display: grid;
  gap: var(--double-unit);
  grid-template-columns: 1fr;
  /* only for mobile */
  &.twoColumnsOnMobile {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(${itemWidth}px, 1fr));

    /* the following rules are for where the grid is full width */
    /* it ensures that each line has the same number of cards regardless of the screen size */
    ${props =>
      props.$columns &&
      css`
        grid-template-columns: repeat(${props.$columns}, minmax(0, 1fr));
      `}
  }
`;

const AnimatedGridContent = styled(Flipper)<{ $columns?: number }>`
  ${gridCss}
`;

export const AnimatedGrid = (props: Flipper['props']) => {
  const sizeRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState<number | undefined>(undefined);
  const { results } = useHits();

  const handleResize = useCallback(() => {
    if (sizeRef.current && results) {
      const itemsCount = sizeRef.current.nextSibling!.childNodes.length;

      if (results.hitsPerPage !== itemsCount) {
        // we're on the last page, just auto-fill
        setColumns(undefined);
        return;
      }

      // Compute the ideal number of columns dividing the width of the container by the width of an item
      // as each item is `itemWidth` wide + gap of `doubleUnitGap` between each, we add a gap of `doubleUnitGap` to
      // make the division easier
      let c = Math.round(
        (sizeRef.current.offsetWidth + doubleUnitGap) /
          (itemWidth + doubleUnitGap)
      );
      // ensure that the last line is full
      while (c > 1 && itemsCount % c > 0) {
        c -= 1;
      }
      setColumns(c);
    }
  }, [results]);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  useResizeObserver(sizeRef, handleResize);

  return (
    <>
      <div ref={sizeRef} />
      <AnimatedGridContent $columns={columns} {...props} />
    </>
  );
};

export const Grid = styled.div<{ $columns?: number }>`
  ${gridCss}
`;

const onAppear = (el: HTMLElement) => {
  el.animate?.([{ opacity: 0 }, { opacity: 1 }], {
    duration: 400,
  });
  el.style.opacity = '1';
};

export const AnimatedGridItem = ({
  children,
  flipId,
  onMouseEnter,
  onMouseLeave,
}: {
  children: ReactNode;
  flipId: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  return (
    <Flipped onAppear={onAppear} flipId={flipId}>
      <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {children}
      </div>
    </Flipped>
  );
};

export const GridItem = ({
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  children: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
    </div>
  );
};
